class LabelsQuote extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.fields = [...this.querySelectorAll('[data-spec]')];
    this.summary = this.querySelector('[data-summary]');
    this.list = this.querySelector('[data-summary-list]');
    const params = new URLSearchParams(location.search);
    const material = this.fields.find(input => input.dataset.spec === 'material');
    if (material && ['paper', 'pp'].includes(params.get('material'))) {
      material.value = [...material.options].find(option => option.dataset.material === params.get('material'))?.value || material.value;
    }
    this.pending = this.querySelector('[data-dimensions-pending]');
    this.design = this.querySelector('[data-design]');
    if (params.get('repeat') === '1') {
      const reference = this.querySelector('[data-reference]');
      this.querySelector('[data-repeat-context]').hidden = false;
      this.querySelector('[data-intent]').value = 'Reposición';
      if (reference) {
        reference.required = true;
        reference.closest('label').firstChild.textContent = this.dataset.referenceRequired;
      }
    }
    this.form.addEventListener('input', () => this.update(), {signal:this.abort.signal});
    this.form.addEventListener('change', () => this.update(), {signal:this.abort.signal});
    this.form.addEventListener('reset', () => queueMicrotask(() => { if (this.isConnected) this.update(); }), {signal:this.abort.signal});
    this.form.addEventListener('submit', event => {
      if (this.dataset.enabled !== 'true' || !this.querySelector('input[name="contact[Privacidad]"]')) {
        event.preventDefault();
        return;
      }
      this.update();
      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
      }
    }, {signal:this.abort.signal});
    this.querySelector('[data-download]')?.addEventListener('click', () => this.download(), {signal:this.abort.signal});
    this.querySelector('[data-export]')?.addEventListener('click', () => this.exportProject(), {signal:this.abort.signal});
    this.querySelector('[data-import]')?.addEventListener('change', event => this.importProject(event), {signal:this.abort.signal});
    this.querySelectorAll('[data-enhancement]').forEach(element => { element.hidden = false; });
    if (this.summary) this.summary.hidden = false;
    this.update();
  }
  disconnectedCallback() { this.abort?.abort(); }
  label(field) { return field.closest('label')?.childNodes[0]?.textContent?.trim() || field.name; }
  value(field) {
    if (field.disabled) return this.dataset.undefined;
    return field.tagName === 'SELECT' ? field.selectedOptions[0]?.textContent : field.value;
  }
  update() {
    const shape = this.fields.find(input => input.dataset.spec === 'shape');
    const width = this.fields.find(input => input.dataset.spec === 'width');
    const height = this.fields.find(input => input.dataset.spec === 'height');
    for (const dimension of [width, height]) {
      if (!dimension) continue;
      dimension.disabled = Boolean(this.pending?.checked);
      dimension.required = !dimension.disabled;
    }
    if (height && shape) {
      height.readOnly = shape.selectedOptions[0]?.dataset.shape === '1' && !height.disabled;
      if (height.readOnly && width) height.value = width.value;
    }
    if (this.design) {
      let valid = !this.design.value;
      try {
        const url = new URL(this.design.value);
        valid = url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password;
      } catch {}
      this.design.setCustomValidity(valid ? '' : this.dataset.designInvalid);
    }
    if (!this.list) return;
    this.list.replaceChildren();
    for (const field of this.fields) {
      const dt = document.createElement('dt');
      dt.textContent = this.label(field);
      const dd = document.createElement('dd');
      dd.textContent = this.value(field) || '—';
      this.list.append(dt, dd);
    }
  }
  validateSpec() {
    this.update();
    const invalid = this.fields.find(field => !field.checkValidity());
    if (invalid) { invalid.reportValidity(); invalid.focus(); return false; }
    return true;
  }
  saveFile(content, type, filename) {
    const blob = new Blob([content], {type});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename;
    document.body.append(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  download() {
    if (!this.validateSpec()) return;
    const lines = [this.dataset.downloadBrand, this.dataset.downloadPending, ''];
    for (const field of this.fields) lines.push(this.label(field) + ': ' + (this.value(field) || this.dataset.undefined));
    this.saveFile(lines.join('\n'), 'text/plain;charset=utf-8', 'sinete-especificacion-etiquetas.txt');
  }
  exportProject() {
    if (!this.validateSpec()) return;
    const specs = Object.fromEntries(this.fields.map(field => [field.dataset.spec, field.disabled ? '' : field.value]));
    const project = {format: 'sinete-specification', version: 1, dimensionsPending: Boolean(this.pending?.checked), specs};
    this.saveFile(JSON.stringify(project, null, 2), 'application/json', 'sinete-configuracion.json');
  }
  async importProject(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const status = this.querySelector('[data-import-status]');
    const signal = this.abort.signal;
    const revision = this.importRevision = (this.importRevision || 0) + 1;
    try {
      if (file.size > 16384) throw new Error('size');
      const project = JSON.parse(await file.text());
      if (signal.aborted || revision !== this.importRevision) return;
      if (project.format !== 'sinete-specification' || project.version !== 1 || typeof project.dimensionsPending !== 'boolean' || !project.specs || typeof project.specs !== 'object' || Array.isArray(project.specs)) throw new Error('format');
      const values = this.fields.map(field => {
        const value = project.specs[field.dataset.spec];
        if (typeof value !== 'string' || value.length > 200) throw new Error('value');
        if (field.tagName === 'SELECT') {
          if (![...field.options].some(option => option.value === value)) throw new Error('option');
        } else if (project.dimensionsPending && ['width', 'height'].includes(field.dataset.spec)) {
          if (value !== '') throw new Error('pending dimensions');
        } else {
          const probe = field.cloneNode();
          probe.disabled = false;
          probe.readOnly = false;
          probe.required = true;
          probe.value = value;
          if (probe.value !== value || !probe.checkValidity() || !Number.isFinite(Number(value))) throw new Error('number');
        }
        return [field, value];
      });
      const shape = values.find(([field]) => field.dataset.spec === 'shape');
      const round = [...shape[0].options].find(option => option.value === shape[1])?.dataset.shape === '1';
      if (round && !project.dimensionsPending && project.specs.width !== project.specs.height) throw new Error('circle');
      for (const [field, value] of values) field.value = value;
      this.pending.checked = project.dimensionsPending;
      this.update();
      status.textContent = this.dataset.importSuccess;
    } catch {
      if (!signal.aborted && revision === this.importRevision) status.textContent = this.dataset.importError;
    } finally { if (revision === this.importRevision) event.target.value = ''; }
  }
}
if (!customElements.get('labels-quote')) customElements.define('labels-quote', LabelsQuote);
class LabelsContact extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.querySelector('form')?.addEventListener('submit', event => {
      if (this.dataset.enabled !== 'true' || !this.querySelector('input[name="contact[Privacidad]"]')) event.preventDefault();
    }, {signal: this.abort.signal});
  }
  disconnectedCallback() { this.abort?.abort(); }
}
if (!customElements.get('labels-contact')) customElements.define('labels-contact', LabelsContact);
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  const menu = document.querySelector('.labels-mobile-nav[open]');
  if (menu) { menu.open = false; menu.querySelector('summary')?.focus(); }
});

